import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
  CircularProgress,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import * as yup from "yup";
import { SelectOption } from "./types";

export type FormFieldType =
  | "text"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "textarea"
  | "select"
  | "singleSelect"
  | "multiSelect"
  | "email"
  | "url"
  | "checkbox";

export interface FormFieldOption {
  value: string | number;
  label: string;
}

export interface FormField {
  name: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  defaultValue?: any;
  placeholder?: string;
  options?: FormFieldOption[]; // For select fields (static options)
  optionsKey?: string; // For singleSelect/multiSelect - key to look up in fieldOptions
  min?: number; // For number fields
  max?: number; // For number fields
  step?: number; // For number fields
  rows?: number; // For textarea fields
  disabled?: boolean;
  validation?: yup.AnySchema; // Custom Yup validation schema
  prefillOnDuplicate?: boolean; // Only prefill this field when duplicating
  prefillTransform?: (value: any) => any; // Transform the value when prefilling
  displayContext?: "create" | "duplicate" | "both"; // When to show this field
  disabledWhen?: (
    formData: Record<string, any>,
    isDuplicate: boolean
  ) => boolean; // Dynamic disabled state
}

interface CreateFormModalProps {
  open: boolean;
  onClose: () => void;
  fields: FormField[];
  apiUrl: string;
  onSuccess: (message: string) => void;
  onError: (error: Error) => void;
  duplicateData?: Record<string, any> | null;
  fieldOptions?: Record<string, SelectOption[]>; // Options for singleSelect/multiSelect fields
}

export function CreateFormModal({
  open,
  onClose,
  fields,
  apiUrl,
  onSuccess,
  onError,
  duplicateData = null,
  fieldOptions = {},
}: CreateFormModalProps) {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Initialize form data when modal opens or duplicate data changes
  useEffect(() => {
    if (open) {
      setErrors({}); // Clear errors when opening
      const isDuplicate = duplicateData !== null;

      if (isDuplicate) {
        // Pre-populate with duplicate data
        const initialData: Record<string, any> = {};
        fields.forEach((field) => {
          // Special handling for checkbox type - defaults to checked with duplicate value
          if (field.type === "checkbox") {
            // For checkbox fields, prefill with the duplicate data value (defaults to checked)
            if (duplicateData[field.name] !== undefined) {
              initialData[field.name] = duplicateData[field.name];
            } else {
              initialData[field.name] = field.defaultValue ?? null;
            }
          } else {
            // Only prefill if prefillOnDuplicate is true (or undefined for backwards compatibility)
            const shouldPrefill = field.prefillOnDuplicate;

            if (shouldPrefill && duplicateData[field.name] !== undefined) {
              let value = duplicateData[field.name];
              // Apply transformation if provided
              if (field.prefillTransform) {
                value = field.prefillTransform(value);
              }
              initialData[field.name] = value;
            } else if (field.defaultValue !== undefined) {
              initialData[field.name] = field.defaultValue;
            } else {
              initialData[field.name] = "";
            }
          }
        });
        setFormData(initialData);
      } else {
        // Reset form for new record
        const initialData: Record<string, any> = {};
        fields.forEach((field) => {
          initialData[field.name] = field.defaultValue ?? "";
        });
        setFormData(initialData);
      }
    }
  }, [open, duplicateData, fields]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field when user makes changes
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = async (): Promise<boolean> => {
    const validationErrors: Record<string, string> = {};

    // Validate each field individually to avoid path transformation issues
    for (const field of fields) {
      const value = formData[field.name];
      let schema: yup.AnySchema | null = null;

      if (field.validation) {
        // Use custom validation if provided
        schema = field.validation;
      } else if (field.required) {
        // Default required validation
        switch (field.type) {
          case "number":
            schema = yup
              .number()
              .required(`${field.label} is required`)
              .typeError(`${field.label} must be a number`);
            if (field.min !== undefined) {
              schema = (schema as yup.NumberSchema).min(
                field.min,
                `${field.label} must be at least ${field.min}`
              );
            }
            if (field.max !== undefined) {
              schema = (schema as yup.NumberSchema).max(
                field.max,
                `${field.label} must be at most ${field.max}`
              );
            }
            break;
          case "email":
            schema = yup
              .string()
              .required(`${field.label} is required`)
              .email(`${field.label} must be a valid email`);
            break;
          case "url":
            schema = yup
              .string()
              .required(`${field.label} is required`)
              .url(`${field.label} must be a valid URL`);
            break;
          case "boolean":
            schema = yup.boolean().required(`${field.label} is required`);
            break;
          default:
            schema = yup
              .string()
              .required(`${field.label} is required`)
              .test("not-empty", `${field.label} is required`, (value) => {
                return (
                  value !== undefined &&
                  value !== null &&
                  value.toString().trim() !== ""
                );
              });
        }
      }

      // Validate the field if it has a schema
      if (schema) {
        try {
          await schema.validate(value);
        } catch (error) {
          if (error instanceof yup.ValidationError) {
            validationErrors[field.name] = error.message;
          }
        }
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = async () => {
    // Validate form before submitting
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    setLoading(true);
    try {
      const requestBody = {
        operation: "CREATE",
        data: formData,
      };

      await axios.post(apiUrl, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      onSuccess("Record created successfully");
      onClose();
      setFormData({});
      setErrors({});
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to create record";

      onError(
        axios.isAxiosError(error)
          ? new Error(errorMessage)
          : error instanceof Error
          ? error
          : new Error(errorMessage)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({});
      setErrors({});
      onClose();
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.name] ?? "";
    const error = errors[field.name];
    const hasError = Boolean(error);
    const isDuplicate = duplicateData !== null;

    // Determine if field should be disabled
    const isDisabled =
      loading ||
      field.disabled ||
      (field.disabledWhen ? field.disabledWhen(formData, isDuplicate) : false);

    // Handle different field types
    switch (field.type) {
      case "number":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="number"
            value={value}
            onChange={(e) =>
              handleFieldChange(
                field.name,
                e.target.value ? Number(e.target.value) : ""
              )
            }
            disabled={isDisabled}
            required={field.required}
            placeholder={field.placeholder}
            size="small"
            error={hasError}
            helperText={error}
            slotProps={{
              htmlInput: {
                min: field.min,
                max: field.max,
                step: field.step,
              },
            }}
          />
        );

      case "boolean":
        return (
          <TextField
            key={field.name}
            fullWidth
            select
            label={field.label}
            value={value === true || value === "true" ? "true" : "false"}
            onChange={(e) =>
              handleFieldChange(field.name, e.target.value === "true")
            }
            disabled={isDisabled}
            required={field.required}
            size="small"
            error={hasError}
            helperText={error}
          >
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </TextField>
        );

      case "checkbox": {
        // Checkbox that controls a value - when checked, uses duplicateData, when unchecked, sets to null
        const isChecked = value !== null && value !== undefined && value !== "";

        return (
          <TextField
            key={field.name}
            fullWidth
            select
            label={field.label}
            value={isChecked ? "true" : "false"}
            onChange={(e) => {
              const checked = e.target.value === "true";
              if (
                checked &&
                duplicateData &&
                duplicateData[field.name] !== undefined
              ) {
                // Set to the duplicate data value
                handleFieldChange(field.name, duplicateData[field.name]);
              } else {
                // Set to null
                handleFieldChange(field.name, null);
              }
            }}
            disabled={isDisabled}
            required={field.required}
            size="small"
            error={hasError}
            helperText={error}
          >
            <MenuItem value="false">No</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
          </TextField>
        );
      }

      case "select":
        return (
          <TextField
            key={field.name}
            fullWidth
            select
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            placeholder={field.placeholder}
            size="small"
            error={hasError}
            helperText={error}
          >
            {field.options?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        );

      case "multiSelect": {
        // Get options from fieldOptions using optionsKey or field name
        const optionsKey = field.optionsKey || field.name;
        const availableOptions = fieldOptions[optionsKey] || [];
        const currentValue = (value as SelectOption[]) || [];

        return (
          <Autocomplete
            key={field.name}
            multiple
            options={availableOptions}
            value={currentValue}
            onChange={(_event, newValue) =>
              handleFieldChange(field.name, newValue)
            }
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                required={field.required}
                placeholder={field.placeholder}
                size="small"
                error={hasError}
                helperText={error}
              />
            )}
            slotProps={{
              chip: {
                size: "small",
                sx: { height: 20, fontSize: "0.75rem" },
              },
            }}
            disableCloseOnSelect
          />
        );
      }

      case "singleSelect": {
        // Get options from fieldOptions using optionsKey or field name
        const optionsKey = field.optionsKey || field.name;
        const availableOptions = fieldOptions[optionsKey] || [];
        const currentValue = (value as SelectOption | null) || null;

        return (
          <Autocomplete
            key={field.name}
            options={availableOptions}
            value={currentValue}
            onChange={(_event, newValue) =>
              handleFieldChange(field.name, newValue)
            }
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            disabled={isDisabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                required={field.required}
                placeholder={field.placeholder}
                size="small"
                error={hasError}
                helperText={error}
              />
            )}
          />
        );
      }

      case "date":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            size="small"
            error={hasError}
            helperText={error}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        );

      case "datetime":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="datetime-local"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            size="small"
            error={hasError}
            helperText={error}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        );

      case "textarea":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            placeholder={field.placeholder}
            size="small"
            error={hasError}
            helperText={error}
            multiline
            rows={field.rows ?? 3}
          />
        );

      case "email":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="email"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            placeholder={field.placeholder}
            size="small"
            error={hasError}
            helperText={error}
          />
        );

      case "url":
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            type="url"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            placeholder={field.placeholder}
            size="small"
            error={hasError}
            helperText={error}
          />
        );

      case "text":
      default:
        return (
          <TextField
            key={field.name}
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            disabled={isDisabled}
            required={field.required}
            placeholder={field.placeholder}
            size="small"
            error={hasError}
            helperText={error}
          />
        );
    }
  };

  const isDuplicate = duplicateData !== null;

  // Filter fields based on displayContext
  const visibleFields = fields.filter((field) => {
    const context = field.displayContext || "both";
    if (context === "both") return true;
    if (context === "duplicate") return isDuplicate;
    if (context === "create") return !isDuplicate;
    return true;
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        {duplicateData ? "Duplicate Record" : "Create New Record"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {visibleFields.map((field) => (
              <Grid size={{ xs: 12, sm: 6 }} key={field.name}>
                {renderField(field)}
              </Grid>
            ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
